/**
 * How a failed dashboard READ is reported to Sentry.
 *
 * WHY THIS EXISTS — half the dashboard's failure surface was invisible
 * --------------------------------------------------------------------
 * `App.tsx` installed a global `MutationCache.onError` and, until this module,
 * **no `QueryCache.onError` at all**. So a failed WRITE reached Sentry and a
 * failed READ reached nothing: no capture, no breadcrumb, no tag. The page
 * rendered whatever its `isError` branch renders and the operator was the only
 * one who ever knew.
 *
 * That is not theoretical. `useGetLocation` requested a route the API does not
 * have (`locations/:id`; there is no `@Controller('locations')`), so the
 * location edit page answered 404 for **every id, in every environment, since
 * the hook was written** — and no Sentry issue exists for it, which is why it
 * took a code read to find rather than a triage pass (ledger run 65, still
 * unmerged in #567). A permanently broken GET should not depend on somebody
 * reading the right file.
 *
 * ⚠️ A 4xx MEANS THE OPPOSITE ON A READ AND ON A WRITE, WHICH IS WHY THIS IS NOT
 * `classifyApiError`
 * ------------------------------------------------------------------------------
 * On a mutation, a 4xx is the API refusing an action the operator deliberately
 * took, and telling them why — an outcome, already shown in a toast, correctly
 * filtered to a breadcrumb by `apiErrorReporting.ts`.
 *
 * On a query, nobody asked for anything: the client composed the URL. A 400 or a
 * 404 on a GET therefore means the CLIENT asked wrong — a route that does not
 * exist, a `NaN` pasted into a path segment, a missing param — which is exactly
 * the class this routine has now fixed on five separate pages (DASH-LOCPATH,
 * MOB-LOCPATH, R79-4, R84-1, and the `useGetLocation` 404 above). Inheriting the
 * mutation rule here would file every one of them as a breadcrumb: the same
 * silence this module exists to end, restated as a policy.
 *
 * So reads capture everything EXCEPT the two statuses that are genuinely
 * expected on a read (ledger D2 — never 5xx, never data corruption, never
 * money):
 *   - **401** — an expired id token. `apiClient` refreshes and retries; the
 *     operator never sees it and there is no defect.
 *   - **403** — a role-scoped page. Coaches share the admin layout and are
 *     refused the sections `routes/access-policy.ts` does not grant them.
 * Both still leave a breadcrumb, so nothing is silently dropped.
 *
 * ⚠️ AND ONE EXCEPTION KEYED ON THE API'S OWN CODE, NOT ON A STATUS — DASHBOARD-3Y
 * -------------------------------------------------------------------------------
 * The premise above ("a 4xx on a read means the client asked wrong") is a
 * statement about the CALLER, and the API has one refusal that contradicts it
 * explicitly: `BOOKING_NOT_PRICEABLE`. `unpriceable-booking.exception.ts` calls
 * it "an ordinary state of a live venue" and exists to make it nameable. The
 * operator did choose the court, the sport and the capacity; the 404 is about
 * the venue's pricing rows, and the booking modal already tells them which
 * knob to turn. Capturing it filed a dashboard defect for a data state no
 * engineer can fix — while the API drops the identical 4xx on its own side.
 *
 * It is NOT a status carve-out: 404 stays captured, because 404 is also the
 * route-does-not-exist class this module was written for. See
 * `utils/unpriceableBooking.ts` for why the code and not the message.
 * Breadcrumbed, not dropped (D2), and the only code in the set.
 *
 * TWO MORE DIFFERENCES FROM THE MUTATION PATH, both deliberate:
 *
 * 1. **No toast.** A mutation is a user action, so a failure gets a toast. A
 *    query refetches on mount, on window focus and on reconnect, none of which
 *    the operator asked for; every page already renders its own error state.
 * 2. **An offline browser is not a defect.** `navigator.onLine === false` is the
 *    browser saying the failure is not ours, so those become breadcrumbs too. A
 *    mutation keeps reporting offline: the operator pressed a button and
 *    deserves to have the failure recorded.
 *    **Narrower than it first looks, and measured rather than assumed** (see
 *    `queryClient.test.ts`): React Query v4 defaults to `networkMode: 'online'`
 *    and consults `onlineManager` BEFORE fetching, so the wake-from-sleep
 *    refetch storm this rule was written for is mostly *paused*, not failed.
 *    What is left — and what this covers — is the case react-query cannot pause:
 *    a request already in flight when connectivity drops.
 */
import type { Breadcrumb } from '@sentry/react';
import { ApiError, ApiRequestFailure } from '../apiClient';
import {
    apiErrorCaptureContext,
    captureContextFor,
    classifyApiError,
    templateEndpoint,
} from './apiErrorReporting';
import { shouldReportToSentry } from './expectedErrors';
import { BOOKING_NOT_PRICEABLE_CODE } from './unpriceableBooking';

/**
 * The only statuses a READ is allowed to fail with silently. Everything else —
 * including every other 4xx — is a defect until proven otherwise.
 */
export const EXPECTED_READ_STATUSES: ReadonlySet<number> = new Set([401, 403]);

/**
 * The API wire codes that declare a refusal to be a venue-data state rather
 * than a fault, whatever status carries them (DASHBOARD-3Y). Deliberately a
 * one-element set rather than a bare `if`: the next such code should be an
 * entry here, with its own test, not a second branch.
 */
export const EXPECTED_READ_CODES: ReadonlySet<string> = new Set([
    BOOKING_NOT_PRICEABLE_CODE,
]);

/** What the caller should do with a failed query. */
export type QueryFailureReport =
    | { kind: 'breadcrumb'; breadcrumb: Breadcrumb }
    | {
        kind: 'capture';
        context?: {
            fingerprint: string[];
            tags: Record<string, string>;
            extra: Record<string, unknown>;
        };
    }
    | { kind: 'skip'; reason: 'expected-auth-outcome' };

/**
 * A short description of the failed request, for the offline breadcrumb.
 *
 * `ApiRequestFailure` is included because the offline breadcrumb is the ONE
 * place a transport failure is legitimately not an error — and before that type
 * existed it was also the one failure this function could not describe, so
 * every offline breadcrumb for a dropped request read `a query failed while the
 * browser reported itself offline` and named nothing (DASHBOARD-1V / -40).
 */
function describeRequest(error: unknown): string {
    const request =
        error instanceof ApiRequestFailure
            ? error.request
            : error instanceof ApiError
                ? error.request
                : undefined;
    if (!request) return 'a query';
    return `${request.method.toUpperCase()} ${templateEndpoint(request.endpoint)}`;
}

/**
 * Classifies a query failure.
 *
 * `online` is `navigator.onLine` at the moment of the failure; it defaults to
 * `true` so that an environment without `navigator` (jsdom without the flag, a
 * future SSR pass) reports rather than swallows.
 */
export function classifyQueryFailure(
    error: unknown,
    options: { online?: boolean } = {},
): QueryFailureReport {
    const online = options.online ?? true;

    if (!online) {
        return {
            kind: 'breadcrumb',
            breadcrumb: {
                category: 'query',
                type: 'http',
                level: 'warning',
                message: `${describeRequest(error)} failed while the browser reported itself offline`,
                data: {
                    online: false,
                    status:
                        error instanceof ApiError || error instanceof ApiRequestFailure
                            ? error.status
                            : undefined,
                },
            },
        };
    }

    if (error instanceof ApiError) {
        // Before the status, because the status is 404 and 404 is exactly the
        // client-bug class the `else` branch below is for (DASHBOARD-3Y).
        // D2 is absolute about 5xx, and a code is a weaker guarantee than a
        // status: the API declares this one on a `NotFoundException`, but a
        // future rail that attaches it to a 500 must not inherit the
        // exemption. So the code exempts only inside the 4xx band.
        if (
            error.code !== undefined &&
            EXPECTED_READ_CODES.has(error.code) &&
            error.status >= 400 &&
            error.status < 500
        ) {
            return {
                kind: 'breadcrumb',
                breadcrumb: {
                    category: 'api',
                    type: 'http',
                    level: 'warning',
                    message: `${describeRequest(error)} has no price for this slot (${
                        error.status
                    }): ${error.message}`,
                    data: {
                        endpoint: error.request?.endpoint,
                        method: error.request?.method,
                        status: error.status,
                        code: error.code,
                    },
                },
            };
        }
        if (EXPECTED_READ_STATUSES.has(error.status)) {
            const expected = classifyApiError(error);
            // 401/403 always classify as expected, but fall back rather than
            // assume it: an unreported failure is what this module exists to
            // stop.
            if (expected?.kind === 'expected') {
                return { kind: 'breadcrumb', breadcrumb: expected.breadcrumb };
            }
        } else {
            return { kind: 'capture', context: apiErrorCaptureContext(error) };
        }
    }

    if (!shouldReportToSentry(error)) {
        return { kind: 'skip', reason: 'expected-auth-outcome' };
    }

    // `captureContextFor` covers BOTH failure types. Reached by an
    // `ApiRequestFailure` of either kind, including an `unreadable` 401/403:
    // those two statuses are breadcrumbed above only inside the `ApiError`
    // branch, and that exemption's premise ("apiClient refreshes and retries,
    // the operator never sees it") does not hold here — reaching this throw
    // means the refresh AND the retry both failed and the operator was signed
    // out, which is the session-wedge class, not a silent non-event.
    return { kind: 'capture', context: captureContextFor(error) };
}
